import PacksReportCustomerDrilldown from "./PacksReportCustomerDrilldown";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";

import { bindActionCreators } from "@reduxjs/toolkit";

import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import { TextTruncate } from "components";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { Tooltip } from "components/common/tooltip";
import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type ColumnOption } from "interfaces/columnOptions";
import { bindAction } from "interfaces";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { setIsReportEnabled, reset } from "features/Reporting/state/toolbar/toolbarSlice";
import { createLicensesAccountsFetcher, useLicensesEngagementQuery } from "./Queries/queries";
import { type LicenseAccountRow } from "features/Reporting/types/packs";
import { usePerformanceBreadcrumbs } from "features/Library/Common/Hooks/usePerformanceBreadcrumbs";
import { dateRangeAllTime, isBsi, normalizePackStatusFilter } from "features/Library/Common/utils/performanceUtils";
import type { PackIdAndName } from "./types";

import styles from "./packsReport.module.scss";
import CardReporting from "components/cards/CardReporting/CardReporting";
import ReportingFilter from "components/reportingFilter/ReportingFilter";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";
import ReportingExport from "components/reportingExport/ReportingExport";
import cn from "classnames";
import { doLicensesAccountsExport } from "./Queries/doLicensesAccountPacksExport";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";

const PacksReport: FC<PropsFromRedux> = ({
  accountId,
  setExportAction,
  setHasExportData,
  setIsReportEnabled,
  resetIsReportEnabled,
  accountType,
  licensesAccountsExport,
}) => {
  const reportEnabled = useFeatureFlag(FeatureFlags.PacksReport);
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType, true);
  const initialDateRange = useMemo(() => dateRangeAllTime(), []);
  const initialOverrides = useMemo(() => (!isBsi(accountId) ? { type: "account" as const } : undefined), [accountId]);
  const [filter, setFilter] = usePerformanceFilter({
    accountId,
    includeAccountsDropdown: accountFilterShouldShow,
    supportsSPType: true,
    includePacks: true,
    initialDateRange,
    initialOverrides,
  });

  const [selectedAccount, setSelectedAccount] = useState<{ id: number; name: string } | null>(null);
  const [selectedPack, setSelectedPack] = useState<PackIdAndName | null>(null);

  const { domElements, registerBreadcrumb, hasBreadcrumbsToDisplay } = usePerformanceBreadcrumbs();

  const normalizedFilter = useMemo(
    () => ({
      ...filter,
      packStatusId: normalizePackStatusFilter(filter.packStatusId!),
    }),
    [filter],
  );

  const engagementQuery = useLicensesEngagementQuery(normalizedFilter);

  const fetchAccountsPage = useMemo(() => createLicensesAccountsFetcher(normalizedFilter), [normalizedFilter]);
  const queryKeyBase = useMemo(() => ["LicensesAccounts", normalizedFilter] as const, [normalizedFilter]);

  useEffect(() => {
    if (selectedAccount === null) {
      setExportAction({
        method: licensesAccountsExport,
        args: [normalizedFilter, "Licenses Accounts Export"],
        isExportEnabled: true,
      });
    }
  }, [selectedAccount, setExportAction, licensesAccountsExport, normalizedFilter]);

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) =>
      setHasExportData({
        hasDataToExport,
        tooltipContent: "No data available for export. This packs report contains no data.",
      }),
    [setHasExportData],
  );

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, resetIsReportEnabled, setIsReportEnabled]);

  const handleAccountSelection = useCallback(
    (row: LicenseAccountRow) => {
      setSelectedAccount({ id: row.CustomerId, name: row.CustomerName });
      registerBreadcrumb({
        text: row.CustomerName,
        action: () => {
          setSelectedAccount(null);
          setSelectedPack(null);
        },
      });
    },
    [registerBreadcrumb],
  );

  useEffect(() => {
    if (selectedPack) {
      registerBreadcrumb({ text: selectedPack.packName, action: () => setSelectedPack(null) });
    }
  }, [selectedPack, registerBreadcrumb]);

  const columnOptions = useMemo<ColumnOption<LicenseAccountRow>[]>(
    () => [
      {
        name: "Customer",
        width: 4,
        isSortable: true,
        sortKey: "CustomerName",
        render(entity) {
          return (
            <Tooltip
              target={
                <button className={styles.linkButton} onClick={() => handleAccountSelection(entity)}>
                  <TextTruncate className={styles.wordBreak}>{entity.CustomerName}</TextTruncate>
                </button>
              }
              content={entity.CustomerName}
              showAlways
            />
          );
        },
      },
      {
        name: "Free Packs",
        width: 3,
        isSortable: true,
        sortKey: "FreePacks",
        render: ({ FreePacks }) => FreePacks.toLocaleString(),
      },
      {
        name: "Other Packs",
        width: 3,
        isSortable: true,
        sortKey: "OtherPacks",
        render: ({ OtherPacks }) => OtherPacks.toLocaleString(),
      },
      {
        name: "Paid Packs",
        width: 3,
        isSortable: true,
        sortKey: "PaidPacks",
        render: ({ PaidPacks }) => PaidPacks.toLocaleString(),
      },
      {
        name: "Trial Packs",
        width: 3,
        isSortable: true,
        sortKey: "TrialPacks",
        render: ({ TrialPacks }) => TrialPacks.toLocaleString(),
      },
      {
        name: "Total Packs",
        width: 3,
        isSortable: true,
        sortKey: "TotalPacks",
        render: ({ TotalPacks }) => TotalPacks.toLocaleString(),
      },
    ],
    [handleAccountSelection],
  );

  const customerFilter = selectedAccount
    ? {
        ...normalizedFilter,
        accounts: [selectedAccount.id],
        includeMyData: false,
      }
    : null;

  if (!reportEnabled) {
    return (
      <div className={styles.reportUnavailable}>
        <ReportUnavailable />
      </div>
    );
  }

  return (
    <div className={"performanceRoot"}>
      <div className="performanceHeader">
        <div className={cn("performanceHeader", styles.header)}>
          {hasBreadcrumbsToDisplay ? (
            <div className="performanceBreadcrumbs">{domElements}</div>
          ) : (
            <h2 className="performanceTitle">Summary</h2>
          )}
          {!selectedPack && (
            <div className="performanceActions">
              {/* @ts-ignore */}
              <ReportingFilter
                currentFilter={filter}
                callback={setFilter}
                includeAccountsDropdown={accountFilterShouldShow}
                ignoreInCountAndHide={
                  selectedAccount ? ["type", "accounts", "includeMyData", "dates"] : ["type", "dates"]
                }
                supportsSPType
                includePacks
                initialDateRange={initialDateRange}
                overrideAccountId={selectedAccount?.id}
                initialOverrides={initialOverrides}
              />
              <ReportingExport />
            </div>
          )}
        </div>
      </div>
      <div className={styles.content}>
        {selectedAccount ? (
          <PacksReportCustomerDrilldown
            filter={customerFilter!}
            setSelectedPack={setSelectedPack}
            selectedPack={selectedPack}
            selectedAccount={selectedAccount}
          />
        ) : (
          <>
            <div className={styles.cardsSection}>
              <RequestStatusRenderer state={engagementQuery.status}>
                {engagementQuery.isSuccess && engagementQuery.data && (
                  <CardReporting items={engagementQuery.data.packsCards} />
                )}
              </RequestStatusRenderer>
            </div>

            <div className={styles.tableSection}>
              <GenericPerformanceListWithServerPagination
                title="Metrics by Customer"
                columns={columnOptions}
                filter={normalizedFilter}
                fetchPage={fetchAccountsPage}
                queryKeyBase={queryKeyBase}
                hasDataForExport={updateExportDataState}
                isDateAllTime
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
  accountType: state.userProfile.accountTypeId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setExportAction: bindAction(setExportAction, dispatch),
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
  licensesAccountsExport: bindActionCreators(doLicensesAccountsExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PacksReport);
export default ConnectedComponent;
